import { useState, useEffect } from 'react';
import { showConfirm, showAlert } from '@/components/AppDialog';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/utils/env';
import { BRAND } from '@/brand';
import AdminSidebar from '../components/AdminSidebar';
import AdminProfileMenu from '../components/AdminProfileMenu';

/* Admin Profile — "Your account" — ported from the redesigned system-tabs mockup.
   Identity + Save/Discard, change-password with strength meter + confirm, active
   sessions, and a guarded danger zone. Mockup design scoped under `.adminx`. */

const IS_CORP = BRAND === 'corporate';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Owner', admin: 'Full admin', full: 'Full admin', ops: 'Operations', finance: 'Finance', catalog: 'Catalog & content', support: 'Support',
};

const CSS = `
.adminx{font-family:'Poppins',system-ui,sans-serif;color:#0F172A}
.adminx .ph{margin-bottom:20px}
.adminx .ph h1{font-size:23px;font-weight:700;letter-spacing:-.02em}
.adminx .ph p{font-size:13.5px;color:#64748B;margin-top:3px}
.adminx .card{background:#fff;border:1px solid #E6E9ED;border-radius:14px;box-shadow:0 1px 2px rgba(15,23,42,.05);margin-bottom:18px}
.adminx .card-h{padding:16px 20px;border-bottom:1px solid #EFF1F4}
.adminx .card-h b{font-size:14.5px;font-weight:600;letter-spacing:-.01em}
.adminx .card-h span{font-size:12.5px;color:#64748B;display:block;margin-top:1px;font-weight:400}
.adminx .card-b{padding:20px}
.adminx .card-f{padding:14px 20px;border-top:1px solid #EFF1F4;display:flex;align-items:center;gap:10px;background:#FCFCFD;border-radius:0 0 14px 14px}
.adminx .card-f .note{font-size:12px;color:#64748B;margin-right:auto}
.adminx .grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.adminx .f{margin-bottom:16px}.adminx .f:last-child{margin-bottom:0}
.adminx .f label{display:block;font-size:12.5px;font-weight:600;color:#334155;margin-bottom:6px}
.adminx .f .hint{font-size:11.5px;color:#64748B;margin-top:5px}
.adminx .inp{width:100%;border:1px solid #E6E9ED;border-radius:9px;padding:10px 12px;background:#fff;transition:.14s;font:inherit;color:#0F172A}
.adminx .inp:hover{border-color:#CBD5E1}
.adminx .inp:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .inp.bad{border-color:#DC2626;box-shadow:0 0 0 3px #FEF2F2}
.adminx .pw{position:relative}.adminx .pw .inp{padding-right:42px}
.adminx .pw .eye{position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:#94A3B8;padding:6px;border-radius:6px}
.adminx .err{font-size:11.5px;color:#DC2626;font-weight:500;margin-top:5px;display:none}.adminx .err.on{display:block}
.adminx .btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;border:1px solid #E6E9ED;background:#fff;border-radius:9px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:.14s;white-space:nowrap;font-family:inherit;color:#334155}
.adminx .btn:hover{border-color:#CBD5E1;background:#FAFBFC}
.adminx .btn-pri{background:var(--brand);border-color:var(--brand);color:#fff}
.adminx .btn-pri:hover{background:var(--brand-d);border-color:var(--brand-d)}
.adminx .btn-danger{color:#DC2626;border-color:#FCDCDC;background:#fff}
.adminx .btn-danger:hover{background:#FEF2F2;border-color:#DC2626}
.adminx .btn-danger-solid{background:#DC2626;border-color:#DC2626;color:#fff}
.adminx .btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none}
.adminx .ident{display:flex;align-items:center;gap:16px;padding:20px;border-bottom:1px solid #EFF1F4;flex-wrap:wrap}
.adminx .ident .big{width:56px;height:56px;border-radius:50%;background:linear-gradient(135deg,var(--brand),var(--brand-lt));color:#fff;display:grid;place-items:center;font-size:19px;font-weight:700;flex:none}
.adminx .ident h2{font-size:17px;font-weight:600;letter-spacing:-.02em}
.adminx .ident .em{font-size:13px;color:#64748B}
.adminx .pill{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:4px 11px;font-size:11.5px;font-weight:600}
.adminx .pill i{width:6px;height:6px;border-radius:50%;display:block}
.adminx .pill.ok{background:#E9F7EF;color:#15803D}.adminx .pill.ok i{background:#15803D}
.adminx .pill.role{background:var(--brand-soft);color:var(--brand-d)}.adminx .pill.role i{background:var(--brand)}
.adminx .meta{display:grid;grid-template-columns:repeat(4,1fr);border-top:1px solid #EFF1F4}
.adminx .meta div{padding:14px 20px;border-right:1px solid #EFF1F4}
.adminx .meta div:last-child{border-right:none}
.adminx .meta span{font-size:11px;color:#64748B;font-weight:500;display:block}
.adminx .meta b{font-size:13px;font-weight:600;display:block;margin-top:2px}
.adminx .danger{border-color:#FCDCDC}
.adminx .danger .card-h{border-bottom-color:#FCDCDC;background:#FEF2F2;border-radius:14px 14px 0 0}
.adminx .danger .card-h b{color:#DC2626}
.adminx .danger .card-b p{font-size:13px;color:#334155;max-width:560px}
.adminx .meter{display:flex;gap:4px;margin-top:9px}
.adminx .meter i{height:4px;flex:1;border-radius:99px;background:#E8ECF1;transition:.2s}
.adminx .meter.s1 i:nth-child(1){background:#DC2626}
.adminx .meter.s2 i:nth-child(-n+2){background:#EA9C1E}
.adminx .meter.s3 i:nth-child(-n+3){background:#3B9E52}
.adminx .meter.s4 i{background:#15803D}
.adminx .reqs{list-style:none;margin:10px 0 0;padding:0;display:grid;grid-template-columns:1fr 1fr;gap:4px}
.adminx .reqs li{font-size:11.5px;color:#64748B;display:flex;align-items:center;gap:6px;font-weight:500}
.adminx .reqs li:before{content:"";width:13px;height:13px;border-radius:50%;border:1.5px solid #D7DDE5;flex:none}
.adminx .reqs li.met{color:#15803D}
.adminx .reqs li.met:before{border-color:#15803D;background:#15803D}
.adminx .protect{background:#FFF8E7;border:1px solid #F2E3B8;border-radius:9px;padding:11px 13px;font-size:12.5px;color:#7a5c00;font-weight:500}
.adminx .tbl{width:100%;border-collapse:collapse}
.adminx .tbl td{padding:13px 20px;border-bottom:1px solid #EFF1F4;font-size:13px}
.adminx .tbl tr:last-child td{border-bottom:none}
.adminx .tbl .nm{font-weight:600}.adminx .tbl .sub{font-size:11.5px;color:#64748B}
@media(max-width:900px){.adminx .meta{grid-template-columns:1fr 1fr}.adminx .grid2{grid-template-columns:1fr}}
`;

const initials = (s: string) => (s || '').trim().split(/\s+/).slice(0, 2).map(x => x[0] || '').join('').toUpperCase() || 'A';
const validEmail = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v).trim());

export default function AdminProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: 'Admin', email: '' });
  const [draft, setDraft] = useState({ name: 'Admin', email: '' });
  const [role, setRole] = useState('admin');
  const [savingP, setSavingP] = useState(false);
  // password
  const [pw, setPw] = useState({ cur: '', next: '', conf: '' });
  const [show, setShow] = useState<Record<string, boolean>>({});
  const [savingPw, setSavingPw] = useState(false);
  // delete
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState('');

  useEffect(() => {
    try {
      const a = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const p = { name: a.displayName || a.name || (a.email ? a.email.split('@')[0] : 'Admin'), email: a.email || '' };
      setProfile(p); setDraft(p); setRole(a.role || 'admin');
    } catch { /* ignore */ }
  }, []);

  const dirty = draft.name !== profile.name || draft.email !== profile.email;
  const profileOk = draft.name.trim() && validEmail(draft.email);

  const saveProfile = () => {
    if (!dirty || !profileOk) return;
    setSavingP(true);
    try {
      const a = JSON.parse(localStorage.getItem('adminUser') || '{}');
      localStorage.setItem('adminUser', JSON.stringify({ ...a, displayName: draft.name, name: draft.name, email: draft.email }));
      setProfile({ ...draft });
      showAlert('Profile saved.', { variant: 'success' });
    } catch { showAlert('Could not save profile.'); }
    finally { setSavingP(false); }
  };

  // password requirements
  const req = { len: pw.next.length >= 8, num: /\d/.test(pw.next), cap: /[A-Z]/.test(pw.next), diff: pw.next.length > 0 && pw.next !== pw.cur };
  const pwScore = Object.values(req).filter(Boolean).length;
  const pwMatch = pw.conf.length > 0 && pw.conf === pw.next;
  const pwOk = pw.cur && pwScore === 4 && pwMatch;

  const savePassword = async () => {
    if (!pwOk) return;
    setSavingPw(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email, oldPassword: pw.cur, newPassword: pw.next }),
      });
      const d = await res.json().catch(() => null);
      if (!res.ok || (d && d.success === false)) throw new Error(d?.error || 'Could not update the password.');
      setPw({ cur: '', next: '', conf: '' });
      await showAlert('Password updated.', { variant: 'success' });
    } catch (e: any) {
      await showAlert(e.message || 'Could not update the password.');
    } finally { setSavingPw(false); }
  };

  const isOwner = role === 'super_admin';
  const roleLabel = ROLE_LABEL[role] || 'Administrator';
  const adminId = (() => { try { return (JSON.parse(localStorage.getItem('adminUser') || '{}').uid || 'ADM-0001'); } catch { return 'ADM-0001'; } })();
  const lastLogin = (() => { try { const t = JSON.parse(localStorage.getItem('adminUser') || '{}').loginTime; return t ? new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'; } catch { return '—'; } })();

  const doDelete = () => {
    setDelOpen(false);
    localStorage.clear();
    navigate('/admin-login');
  };

  const Eye = ({ id }: { id: string }) => (
    <button type="button" className="eye" onClick={() => setShow(s => ({ ...s, [id]: !s[id] }))} aria-label="Show password">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <AdminSidebar />
      <div className="flex-1 xl:ml-64 overflow-x-hidden adminx">
        <style>{CSS}</style>
        <header className="bg-white border-b border-[#E6E9ED] px-4 md:px-6 h-[58px] flex items-center justify-between sticky top-0 z-30">
          <span className="text-[13px] text-[#64748B] font-medium ml-10 xl:ml-0">System / <b className="text-[#0F172A] font-semibold">Admin Profile</b></span>
          <AdminProfileMenu />
        </header>

        <div className="page">
          <div className="ph"><h1>Your account</h1><p>Details, password and access level — all in one place.</p></div>

          {/* Identity */}
          <div className="card">
            <div className="ident">
              <span className="big">{initials(draft.name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2>{draft.name || 'Admin'}</h2>
                <div className="em">{draft.email}</div>
              </div>
              <span className="pill role"><i />{roleLabel}</span>
              <span className="pill ok"><i />Active</span>
            </div>
            <div className="card-b">
              <div className="grid2">
                <div className="f"><label>Full name</label><input className="inp" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} /></div>
                <div className="f"><label>Email address</label>
                  <input className={`inp ${draft.email && !validEmail(draft.email) ? 'bad' : ''}`} type="email" value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))} />
                  <div className="hint">You sign in with this address.</div>
                </div>
              </div>
            </div>
            <div className="card-f">
              <span className="note" style={{ color: dirty && !profileOk ? '#DC2626' : '#64748B' }}>{!dirty ? 'No changes yet.' : (profileOk ? 'Unsaved changes.' : 'Fix the highlighted field.')}</span>
              <button className="btn" disabled={!dirty} onClick={() => setDraft({ ...profile })}>Discard</button>
              <button className="btn btn-pri" disabled={!dirty || !profileOk || savingP} onClick={saveProfile}>{savingP ? 'Saving…' : 'Save changes'}</button>
            </div>
            <div className="meta">
              <div><span>Role</span><b>{IS_CORP ? 'Accounting Admin' : 'Consulting Admin'}</b></div>
              <div><span>Last sign-in</span><b>{lastLogin}</b></div>
              <div><span>Access</span><b>{roleLabel}</b></div>
              <div><span>Admin ID</span><b style={{ fontSize: 11, wordBreak: 'break-all' }}>{String(adminId).slice(0, 12)}</b></div>
            </div>
          </div>

          {/* Change password */}
          <div className="card">
            <div className="card-h"><b>Change password</b><span>Use at least 8 characters, with a number and a capital letter.</span></div>
            <div className="card-b">
              <div className="f"><label>Current password</label>
                <div className="pw"><input className="inp" type={show.cur ? 'text' : 'password'} value={pw.cur} onChange={e => setPw(p => ({ ...p, cur: e.target.value }))} placeholder="Enter current password" /><Eye id="cur" /></div>
              </div>
              <div className="f"><label>New password</label>
                <div className="pw"><input className="inp" type={show.next ? 'text' : 'password'} value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} placeholder="Enter new password" /><Eye id="next" /></div>
                <div className={`meter ${pw.next ? 's' + pwScore : ''}`}><i /><i /><i /><i /></div>
                <ul className="reqs">
                  <li className={req.len ? 'met' : ''}>At least 8 characters</li>
                  <li className={req.num ? 'met' : ''}>Contains a number</li>
                  <li className={req.cap ? 'met' : ''}>Contains a capital letter</li>
                  <li className={req.diff ? 'met' : ''}>Different from the current one</li>
                </ul>
              </div>
              <div className="f"><label>Confirm new password</label>
                <div className="pw"><input className={`inp ${pw.conf.length > 0 && !pwMatch ? 'bad' : ''}`} type={show.conf ? 'text' : 'password'} value={pw.conf} onChange={e => setPw(p => ({ ...p, conf: e.target.value }))} placeholder="Re-enter new password" /><Eye id="conf" /></div>
                <div className={`err ${pw.conf.length > 0 && !pwMatch ? 'on' : ''}`}>Passwords do not match.</div>
              </div>
            </div>
            <div className="card-f">
              <span className="note">You stay signed in on this device.</span>
              <button className="btn" onClick={() => setPw({ cur: '', next: '', conf: '' })}>Cancel</button>
              <button className="btn btn-pri" disabled={!pwOk || savingPw} onClick={savePassword}>{savingPw ? 'Updating…' : 'Update password'}</button>
            </div>
          </div>

          {/* Active sessions */}
          <div className="card">
            <div className="card-h"><b>Active sessions</b><span>Devices currently signed in to this account.</span></div>
            <table className="tbl"><tbody>
              <tr><td><span className="nm">This browser</span><div className="sub">Current session</div></td><td style={{ textAlign: 'right' }}><span className="pill ok"><i />This device</span></td></tr>
            </tbody></table>
          </div>

          {/* Danger zone */}
          <div className="card danger">
            <div className="card-h"><b>Danger zone</b></div>
            <div className="card-b" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <b style={{ fontSize: 13.5 }}>Delete this admin account</b>
                <p style={{ marginTop: 3 }}>Removes access immediately and cannot be undone. Content and audit history created by this account are kept.</p>
              </div>
              {isOwner
                ? <div className="protect">The owner account cannot be deleted — transfer ownership first.</div>
                : <button className="btn btn-danger" onClick={() => { setDelConfirm(''); setDelOpen(true); }}>Delete account</button>}
            </div>
          </div>
        </div>

        {/* Delete modal */}
        {delOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setDelOpen(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} style={{ fontFamily: "'Poppins',system-ui,sans-serif" }}>
              <div className="px-5 py-4 border-b border-[#EFF1F4]"><b className="text-[15px] font-semibold text-[#DC2626]">Delete admin account</b></div>
              <div className="p-5">
                <p className="text-[13px] text-[#334155]">This removes access immediately and cannot be undone.</p>
                <div className="mt-4">
                  <label className="block text-[12.5px] font-semibold text-[#334155] mb-1.5">Type <b>{profile.email}</b> to confirm</label>
                  <input className="w-full border border-[#E6E9ED] rounded-[9px] px-3 py-2.5 text-sm" value={delConfirm} onChange={e => setDelConfirm(e.target.value)} placeholder="Type the email address" autoComplete="off" />
                </div>
              </div>
              <div className="px-5 py-3.5 border-t border-[#EFF1F4] flex justify-end gap-2.5 bg-[#FCFCFD]">
                <button className="btn" onClick={() => setDelOpen(false)}>Cancel</button>
                <button className="btn btn-danger-solid" disabled={delConfirm.trim().toLowerCase() !== profile.email.toLowerCase()} onClick={doDelete}>Delete account</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
